import { CreateAccountResponse, LinkWhatsappInstanceResponse, MessagingServerHealth, MessagingServerList, MessagingServerResponse, PlatformAccountList, PlatformAccountNoteResponse, PlatformAccountResponse, PlatformAiCostResponse, PlatformAuditResponse, PlatformErrorList, PlatformMfaSetupResponse, PlatformMfaStatusResponse, PlatformOverviewResponse, PlatformStatusResponse, PlatformWhatsappInstancesResponse, ResetOwnerPasswordResponse, StartSupportResponse, TenantFeatureList, TenantPagesOffList, } from '@aeris/contracts';
import { request } from "./client.js";
export const fetchPlatformAccounts = async (query, signal) => {
    const params = new URLSearchParams();
    if (query.search !== undefined && query.search !== '')
        params.set('search', query.search);
    if (query.status !== undefined)
        params.set('status', query.status);
    if (query.page !== undefined)
        params.set('page', String(query.page));
    if (query.pageSize !== undefined)
        params.set('pageSize', String(query.pageSize));
    if (query.sort !== undefined)
        params.set('sort', query.sort);
    if (query.direction !== undefined)
        params.set('direction', query.direction);
    const encoded = params.toString();
    return PlatformAccountList.parse(await request(`/api/v1/platform/accounts${encoded.length > 0 ? `?${encoded}` : ''}`, { signal }));
};
export const createPlatformAccount = async (input) => CreateAccountResponse.parse(await request('/api/v1/platform/accounts', { method: 'POST', body: input }));
export const suspendPlatformAccount = async (tenantId, reason) => PlatformAccountResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/suspend`, {
    method: 'POST',
    body: { reason },
})).account;
export const reactivatePlatformAccount = async (tenantId) => PlatformAccountResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/reactivate`, {
    method: 'POST',
})).account;
export const refreshPlatformAccountUsage = async (tenantId) => PlatformAccountResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/usage/refresh`, {
    method: 'POST',
})).account;
/**
 * Uma senha nova para o dono de uma conta (F121).
 *
 * Devolve o corpo INTEIRO, e não só a senha: a tela mostra a quem entregar
 * (`ownerEmail`) e quantas sessões caíram. Um `string` aqui obrigaria a tela a
 * remontar as duas coisas que a rota já sabe.
 */
export const resetPlatformOwnerPassword = async (tenantId) => ResetOwnerPasswordResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/owner/password-reset`, { method: 'POST' }));
/**
 * Entra na conta de um lojista para dar suporte (F169).
 *
 * Depois desta chamada a SESSÃO INTEIRA está dentro da loja do cliente — não
 * há "aba de suporte". Quem chama precisa invalidar a sessão em cache, senão a
 * tela continua desenhando o console com dados que a API já não devolve.
 */
export const startPlatformSupport = async (tenantId, reason) => StartSupportResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/support`, {
    method: 'POST',
    body: { reason },
}));
/**
 * As chaves que uma conta tem ligadas (F169).
 *
 * `PUT` substitui o conjunto inteiro, como as telas de um membro: o que está
 * marcado na tela é o que fica no banco, sem depender de o navegador ter visto
 * o mesmo estado que o servidor.
 */
export const fetchPlatformAccountFeatures = async (tenantId, signal) => TenantFeatureList.parse(await request(`/api/v1/platform/accounts/${tenantId}/features`, { signal })).features;
export const setPlatformAccountFeatures = async (tenantId, features) => TenantFeatureList.parse(await request(`/api/v1/platform/accounts/${tenantId}/features`, {
    method: 'PUT',
    body: { features: [...features] },
})).features;
/** As telas do núcleo que a conta NÃO tem (F172). */
export const fetchPlatformAccountPagesOff = async (tenantId, signal) => TenantPagesOffList.parse(await request(`/api/v1/platform/accounts/${tenantId}/pages`, { signal })).pagesOff;
export const setPlatformAccountPagesOff = async (tenantId, pagesOff) => TenantPagesOffList.parse(await request(`/api/v1/platform/accounts/${tenantId}/pages`, {
    method: 'PUT',
    body: { pagesOff: [...pagesOff] },
})).pagesOff;
/** Contato e anotações da plataforma sobre uma conta (F169). Nunca chega ao lojista. */
export const fetchPlatformAccountNote = async (tenantId, signal) => PlatformAccountNoteResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/notes`, { signal })).note;
export const savePlatformAccountNote = async (tenantId, note) => PlatformAccountNoteResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/notes`, {
    method: 'PUT',
    body: note,
})).note;
/** O custo de IA dos últimos meses, separado por quem paga a chave (F169). */
export const fetchPlatformAiCost = async (tenantId, signal) => PlatformAiCostResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/ai-cost`, { signal })).months;
/**
 * Os servidores de WhatsApp por aparelho vinculado (F192, ADR-0039). O token
 * de administração só entra: `adminToken: null` na edição mantém o gravado.
 */
export const fetchMessagingServers = async (signal) => MessagingServerList.parse(await request('/api/v1/platform/messaging-servers', { signal })).servers;
export const saveMessagingServer = async (serverId, body) => MessagingServerResponse.parse(await request(serverId === null
    ? '/api/v1/platform/messaging-servers'
    : `/api/v1/platform/messaging-servers/${serverId}`, { method: serverId === null ? 'POST' : 'PUT', body }));
export const testMessagingServer = async (serverId) => MessagingServerHealth.parse(await request(`/api/v1/platform/messaging-servers/${serverId}/health`, {
    method: 'POST',
}));
/** As instâncias do servidor, com para onde o webhook de cada uma aponta hoje. */
export const fetchPlatformWhatsappInstances = async (tenantId, serverId, signal) => PlatformWhatsappInstancesResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/whatsapp?serverId=${encodeURIComponent(serverId)}`, { signal })).instances;
export const linkWhatsappInstance = async (tenantId, body) => LinkWhatsappInstanceResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/whatsapp/link`, {
    method: 'POST',
    body,
}));
export const unlinkWhatsappInstance = async (tenantId, accountId) => LinkWhatsappInstanceResponse.parse(await request(`/api/v1/platform/accounts/${tenantId}/whatsapp/${accountId}/unlink`, { method: 'POST' }));
export const fetchPlatformMfa = async (signal) => PlatformMfaStatusResponse.parse(await request('/api/v1/platform/mfa', { signal }));
export const setupPlatformMfa = async () => PlatformMfaSetupResponse.parse(await request('/api/v1/platform/mfa/setup', { method: 'POST' }));
export const activatePlatformMfa = async (code) => PlatformMfaStatusResponse.parse(await request('/api/v1/platform/mfa/activate', { method: 'POST', body: { code } }));
export const fetchPlatformOverview = async (signal) => PlatformOverviewResponse.parse(await request('/api/v1/platform/overview', { signal }));
export const fetchPlatformStatus = async (now = false, signal) => PlatformStatusResponse.parse(await request(`/api/v1/platform/status${now ? '?agora=1' : ''}`, { signal }));
export const fetchPlatformAudit = async (query, signal) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        params.set(key, String(value));
    }
    const encoded = params.toString();
    return PlatformAuditResponse.parse(await request(`/api/v1/platform/audit${encoded.length > 0 ? `?${encoded}` : ''}`, {
        signal,
    })).events;
};
export const fetchPlatformErrors = async (signal) => PlatformErrorList.parse(await request('/api/v1/platform/errors', { signal })).errors;
export const resolvePlatformError = async (id) => {
    await request(`/api/v1/platform/errors/${id}/resolve`, { method: 'POST' });
};
//# sourceMappingURL=platform.js.map